import React, { useState, useEffect } from "react";
import { useForm } from "../../../../hooks/useForm";
import {
  Card,
  Button,
  DateRangePicker,
  Checkbox,
  Select,
  Badge,
  MultiSelect,
  Spinner,
} from "../../../../shared";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
  getQuarter,
} from "date-fns";
import {
  // useGetDriverReportsQuery,
  useGenerateDriverReportMutation,
  // useGetDriverReportQuery,
} from "../../../../redux/services/reports/reportsApi";
import { ReportTable } from "../../../../components";
import request from "../../../../utils/request";
import { payoutReportStage } from "../../../../constants/aggregate";
import { toast } from "react-toastify";
import { useAuth } from "../../../../hooks/useAuth";
import { useConfig } from "../../../../hooks";

/**
 * DriverReports component displays and manages driver-specific reports
 * @param {Object} props - Component props
 * @param {Object} props.driver - Driver object containing driver information
 * @param {string} props.driverStatus - Current status of driver data loading
 */
const DriverReports = ({ driver, driverStatus }) => {
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [dateRangeType, setDateRangeType] = useState(null);
  const [currentReportScope, setCurrentReportScope] = useState("driver");

  // Get parentCompany for PDF generation
  const { user } = useAuth();
  const { configs, parentCompany: parentCompanyId } = useConfig();

  // Get parentCompany object with name if available
  const parentCompany = configs?.parentCompany || {
    _id: parentCompanyId || driver?.parentCompany,
    name: configs?.companyName || "Company",
  };

  const { form, updateForm, isValid } = useForm({
    initialValues: {
      reportScope: "driver", // "driver" or "company"
      reportType: null, // "monthly" or "yearly"
      dateRangeType: "monthly", // monthly, quarterly, yearly, custom
      dateRange: {
        startDate: null,
        endDate: null,
      },
      selectedMonth: new Date(),
      selectedQuarter: getQuarter(new Date()),
      selectedYear: new Date().getFullYear(),
      selectedMonths: [], // For multi-month selection (legacy)
    },
    validations: {
      reportScope: { required: true },
      dateRangeType: { required: true },
    },
  });

  // Calculate date range based on preset type
  const calculateDateRange = React.useCallback((type, month, quarter, year) => {
    let startDate, endDate;

    switch (type) {
      case "monthly":
        const monthDate =
          month instanceof Date ? month : new Date(year, month - 1, 1);
        startDate = startOfMonth(monthDate);
        endDate = endOfMonth(monthDate);
        break;
      case "quarterly":
        const quarterStartMonth = (quarter - 1) * 3;
        startDate = startOfQuarter(new Date(year, quarterStartMonth, 1));
        endDate = endOfQuarter(new Date(year, quarterStartMonth, 1));
        break;
      case "yearly":
        startDate = startOfYear(new Date(year, 0, 1));
        endDate = endOfYear(new Date(year, 0, 1));
        break;
      default:
        return null;
    }

    return { startDate, endDate };
  }, []);

  // Update date range when dateRangeType or related fields change
  useEffect(() => {
    if (form.dateRangeType && form.dateRangeType !== "custom") {
      const range = calculateDateRange(
        form.dateRangeType,
        form.selectedMonth,
        form.selectedQuarter,
        form.selectedYear
      );
      if (range) {
        updateForm({
          id: "dateRange",
          value: range,
        });
        setDateRange(range);
        setDateRangeType(form.dateRangeType);
      }
    }
  }, [
    form.dateRangeType,
    form.selectedMonth,
    form.selectedQuarter,
    form.selectedYear,
    calculateDateRange,
    updateForm,
  ]);

  // Generate month options for selection
  const monthOptions = Array.from({ length: 12 }, (_, i) => {
    const date = new Date(2000, i, 1);
    return {
      value: i + 1,
      label: format(date, "MMMM"),
    };
  });

  // Generate quarter options
  const quarterOptions = [
    { value: 1, label: "Q1 (Jan-Mar)" },
    { value: 2, label: "Q2 (Apr-Jun)" },
    { value: 3, label: "Q3 (Jul-Sep)" },
    { value: 4, label: "Q4 (Oct-Dec)" },
  ];

  // Generate year options (current year and previous 5 years)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => {
    const year = currentYear - i;
    return {
      value: year,
      label: year.toString(),
    };
  });

  // const [generateReport, { data: reports, isLoading }] =
  //   useGenerateDriverReportMutation();

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (!isValid) return;

    // Use date range from form (calculated by useEffect)
    let startDate, endDate;

    if (form.dateRange?.startDate && form.dateRange?.endDate) {
      startDate = form.dateRange.startDate;
      endDate = form.dateRange.endDate;
    } else if (form.dateRangeType && form.dateRangeType !== "custom") {
      // Fallback: calculate date range if not set
      const range = calculateDateRange(
        form.dateRangeType,
        form.selectedMonth,
        form.selectedQuarter,
        form.selectedYear
      );
      if (range) {
        startDate = range.startDate;
        endDate = range.endDate;
      }
    }

    // Validate dates before proceeding
    if (
      !startDate ||
      !endDate ||
      isNaN(startDate.getTime()) ||
      isNaN(endDate.getTime())
    ) {
      console.error("Invalid date range", { startDate, endDate, form });
      toast.error("Please select a valid date range");
      return;
    }

    // Format dates for the API
    const formattedStartDate = startDate.toISOString();
    const formattedEndDate = endDate.toISOString();

    // Store date range for ReportTable
    setDateRange({ startDate, endDate });
    setDateRangeType(form.dateRangeType || "custom");

    const reportScope =
      typeof form.reportScope === "object"
        ? form.reportScope.value
        : form.reportScope;
    setCurrentReportScope(reportScope);
    const driverId = reportScope === "driver" ? driver._id : null;
    // console.log("Report configuration:", {
    //   reportScope: reportScope,
    //   driverId: driverId,
    //   driverName: driver.firstName + " " + driver.lastName,
    // });

    const stages = payoutReportStage(
      formattedStartDate,
      formattedEndDate,
      driver.parentCompany,
      driverId, // Only pass driver ID for driver-specific reports
      reportScope || "driver"
    );

    // console.log("payoutReportStage result:", stages);

    const reportConfig = {
      stages: stages,
      modelName: "Payout",
      config: {
        0: {
          type: "$match",
          useDate: "dateCreated",
          objectId: ["parentCompany", "driver"],
        },
      },
      // Add fallback parameters in case stages is null
      fallbackParams: {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        parentCompany: driver.parentCompany,
        driverId: driver._id,
        driver: driverId, // Add this for payoutReportStage
        reportType: reportScope || "driver", // Use the selected scope (string value)
      },
      populate: true,
      includeVirtuals: true,
    };

    setIsLoading(true);
    setError(null);
    setReportData(null);

    try {
      const response = await request("/reports", {
        method: "post",
        data: reportConfig,
      });

      // console.log("Report response:", response);

      if (response && response.data && Array.isArray(response.data)) {
        setReportData(response);
        toast.success(
          `Report generated successfully! ${response.totalResults} results found.`
        );
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error("Failed to generate report:", {
        error,
        requestData: reportConfig,
        dates: { formattedStartDate, formattedEndDate },
        driver: { parentCompany: driver.parentCompany, id: driver._id },
      });
      setError(error.message || "Failed to generate report");
      toast.error("Failed to generate report. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Check if form is valid
  const isFormValid = () => {
    if (!form.reportScope) return false;
    if (!form.dateRangeType) return false;
    if (!form.dateRange?.startDate || !form.dateRange?.endDate) return false;
    return true;
  };

  return (
    <div className="space-y-6">
      {/* Report Filters Card */}
      <Card className="p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Report Filters
        </h3>

        {/* Top Section: Report Scope */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {/* Report Scope Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Report Scope
            </label>
            <Select
              value={
                typeof form.reportScope === "object"
                  ? form.reportScope.value
                  : form.reportScope
              }
              onChange={(value) => {
                updateForm({
                  id: "reportScope",
                  value: value.value || value,
                });
                setCurrentReportScope(value.value || value);
              }}
              options={[
                {
                  value: "driver",
                  label: `This Driver (${driver.firstName} ${driver.lastName})`,
                },
                { value: "company", label: "All Company Drivers" },
              ]}
              placeholder="Select report scope"
            />
          </div>
        </div>

        {/* Bottom Section: Date Range */}
        <div className="border-t border-gray-200 pt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-4">Date Range</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Date Range Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date Range Type
              </label>
              <Select
                value={
                  form.dateRangeType
                    ? [
                        { value: "monthly", label: "Monthly" },
                        { value: "quarterly", label: "Quarterly" },
                        { value: "yearly", label: "Yearly" },
                        { value: "custom", label: "Custom Range" },
                      ].find((opt) => opt.value === form.dateRangeType)
                        ?.label || ""
                    : ""
                }
                onChange={(value) =>
                  updateForm({
                    id: "dateRangeType",
                    value: value.value,
                  })
                }
                options={[
                  { value: "monthly", label: "Monthly" },
                  { value: "quarterly", label: "Quarterly" },
                  { value: "yearly", label: "Yearly" },
                  { value: "custom", label: "Custom Range" },
                ]}
                placeholder="Select date range type"
              />
            </div>

            {/* Month Selection (for Monthly) */}
            {form.dateRangeType === "monthly" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Month
                </label>
                <Select
                  value={
                    form.selectedMonth instanceof Date
                      ? monthOptions.find(
                          (opt) =>
                            opt.value === form.selectedMonth.getMonth() + 1
                        )?.label || ""
                      : monthOptions.find(
                          (opt) => opt.value === form.selectedMonth
                        )?.label || ""
                  }
                  onChange={(value) =>
                    updateForm({
                      id: "selectedMonth",
                      value: new Date(form.selectedYear, value.value - 1, 1),
                    })
                  }
                  options={monthOptions}
                  placeholder="Select month"
                />
              </div>
            )}

            {/* Quarter Selection (for Quarterly) */}
            {form.dateRangeType === "quarterly" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quarter
                </label>
                <Select
                  value={
                    quarterOptions.find(
                      (opt) => opt.value === form.selectedQuarter
                    )?.label || ""
                  }
                  onChange={(value) =>
                    updateForm({
                      id: "selectedQuarter",
                      value: value.value,
                    })
                  }
                  options={quarterOptions}
                  placeholder="Select quarter"
                />
              </div>
            )}

            {/* Year Selection (for Monthly, Quarterly, and Yearly) */}
            {["monthly", "quarterly", "yearly"].includes(
              form.dateRangeType
            ) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Year
                </label>
                <Select
                  value={
                    yearOptions.find((opt) => opt.value === form.selectedYear)
                      ?.label || ""
                  }
                  onChange={(value) =>
                    updateForm({
                      id: "selectedYear",
                      value: value.value,
                    })
                  }
                  options={yearOptions}
                  placeholder="Select year"
                />
              </div>
            )}

            {/* Custom Date Range Picker */}
            {form.dateRangeType === "custom" && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Date Range
                </label>
                <DateRangePicker
                  startDate={form.dateRange.startDate}
                  endDate={form.dateRange.endDate}
                  onChange={(range) => {
                    const [start, end] = range;
                    updateForm({
                      id: "dateRange",
                      value: {
                        startDate: start,
                        endDate: end,
                      },
                    });
                    setDateRange({
                      startDate: start,
                      endDate: end,
                    });
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end mt-6">
          <Button
            onClick={handleSubmit}
            disabled={isLoading || !isFormValid()}
            className="flex items-center"
          >
            {isLoading ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Generating...
              </>
            ) : (
              "Generate Report"
            )}
          </Button>
        </div>
      </Card>

      {/* Loading State */}
      {isLoading && (
        <Card className="p-6">
          <div className="flex items-center justify-center py-8">
            <Spinner size="lg" className="mr-3" />
            <span className="text-lg">Generating report...</span>
          </div>
        </Card>
      )}

      {/* Error State */}
      {error && (
        <Card className="p-6 border-red-200 bg-red-50">
          <div className="flex items-center text-red-700">
            <svg
              className="w-5 h-5 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <span>Error generating report: {error}</span>
          </div>
        </Card>
      )}

      {reportData && reportData.data && reportData.data.length > 0 && (
        <ReportTable
          data={reportData.data}
          reportName={
            reportData.reportName ||
            `Payout Report - ${
              form.dateRangeType
                ? form.dateRangeType.charAt(0).toUpperCase() +
                  form.dateRangeType.slice(1)
                : "Custom"
            }`
          }
          reportConfig={{
            modelName: reportData.modelName || "Payout",
            config: {},
          }}
          loading={isLoading}
          onRefresh={() => {
            // Re-trigger report generation with current form values
            const syntheticEvent = { preventDefault: () => {} };
            handleSubmit(syntheticEvent);
          }}
          showSummary={true}
          allowExport={true}
          allowSave={true}
          reportMetadata={{
            dateRange: dateRange,
            dateRangeType: dateRangeType,
            driverName:
              currentReportScope === "driver"
                ? `${driver.firstName} ${driver.lastName}`
                : "All Company Drivers",
            reportScope: currentReportScope,
            executedAt: reportData.executedAt,
            generatedFromLegacy: reportData.generatedFromLegacyFormat,
          }}
          parentCompany={parentCompany}
        />
      )}

      {reportData && (!reportData.data || reportData.data.length === 0) && (
        <Card className="p-4 bg-yellow-50 border-yellow-200">
          <div className="flex items-center text-yellow-700">
            <svg
              className="w-5 h-5 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <span>
              No data found for the selected criteria. Try adjusting your date
              range or report type.
            </span>
          </div>
        </Card>
      )}

      {/* Reports List Card */}
      {/* <Card className="p-4">
        <h3 className="text-lg font-semibold mb-4">Previous Reports</h3> */}
      {/* {isLoading ? (
          <div>Loading reports...</div>
        ) : ( */}
      {/* <div className="space-y-2">
          {reports?.map((report) => (
            <div
              key={report._id}
              className="p-3 border rounded hover:bg-gray-50"
            >
              <div className="flex justify-between items-center">
                <div className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <span>
                      {new Date(report.createdAt).toLocaleDateString()}
                    </span>
                    <Badge label={report.reportType} />
                  </div>
                  <div className="text-sm text-gray-500">
                    Metrics: {report.metrics.join(", ")}
                  </div>
                </div>
                <Button size="sm" variant="outline">
                  Download
                </Button>
              </div>
            </div>
          ))}
        </div> */}
      {/* )} */}
      {/* </Card> */}
    </div>
  );
};

export default DriverReports;
